import React, { useState, useRef, useEffect } from "react";
import { Sparkles, Send, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Layout } from "../components/tag/Layout";
import { HeroBg, EyebrowPill } from "../components/tag/Primitives";
import { AIAssistantDisclosure } from "../components/tag/AIDisclosure";
import { api } from "../lib/api";

const OPENING = "I'm Atlas. Tell me a bit about what you already know how to do, or a problem you keep helping people with. I'll listen and help you see what's actually there.";

export default function Atlas() {
  const [messages, setMessages] = useState([{ role: "assistant", content: OPENING }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [signalReached, setSignalReached] = useState(false);
  const [emailPromptOpen, setEmailPromptOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [generating, setGenerating] = useState(false);
  const [authorityMap, setAuthorityMap] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  const send = async (e) => {
    e?.preventDefault();
    if (!input.trim() || busy) return;
    const userMsg = input.trim();
    setMessages((m) => [...m, { role: "user", content: userMsg }]);
    setInput("");
    setBusy(true);
    try {
      const { data } = await api.post("/atlas/chat", { message: userMsg, session_id: sessionId });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
      if (data.authority_signal && !signalReached) {
        setSignalReached(true);
      }
    } catch {
      toast.error("Atlas is temporarily unavailable. Try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  const openEmailPrompt = () => setEmailPromptOpen(true);
  const declineMap = () => setEmailPromptOpen(false);

  const generateMap = async (e) => {
    e?.preventDefault();
    if (!email.trim() || !sessionId) return;
    setGenerating(true);
    try {
      const { data } = await api.post("/atlas/authority-map", {
        session_id: sessionId,
        email: email.trim(),
      });
      setAuthorityMap(data);
    } catch (err) {
      const detail = err?.response?.data?.detail || "We couldn't build your map right now. Please try again.";
      toast.error(detail);
    } finally {
      setGenerating(false);
    }
  };

  // ============ RESULT VIEW ============
  if (authorityMap) {
    const m = authorityMap.map || {};
    return (
      <Layout title="Your Authority Map | The Authority Gap" path="/atlas">
        <section style={{ background: "#090909", padding: "60px 20px 40px", position: "relative", overflow: "hidden", textAlign: "center" }}>
          <HeroBg />
          <div style={{ position: "relative", zIndex: 1, maxWidth: 760, margin: "0 auto" }}>
            <div style={{ marginBottom: 20 }}><EyebrowPill>Your Authority Map</EyebrowPill></div>
            <h1 className="tag-h1" style={{ marginBottom: 12 }}>Here&apos;s what could be here.</h1>
            <p style={{ color: "#aaa", fontSize: 14 }}>Built from your conversation with Atlas. Emailed to <span style={{ color: "#eee" }}>{authorityMap.email}</span>.</p>
          </div>
        </section>

        <section style={{ background: "#090909", padding: "24px 20px 80px" }} data-testid="authority-map-result">
          <div style={{ maxWidth: 760, margin: "0 auto", display: "grid", gap: 12 }}>
            <MapCard label="WHAT YOU KNOW" value={m.what_you_know} testid="map-what-you-know" />
            <MapCard label="THE PROBLEM YOU MAY BE ABLE TO SOLVE" value={m.problem_you_may_be_able_to_solve} testid="map-problem" />
            <MapCard label="WHO COULD PAY FOR IT" value={m.who_could_pay_for_it} testid="map-audience" />
            <MapCard label="WHY YOUR EXPERIENCE MATTERS" value={m.why_your_experience_matters} testid="map-why" />
            <div className="tag-card" data-testid="map-products">
              <div style={{ color: "#FF6575", fontSize: 9, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700, marginBottom: 10 }}>3 POSSIBLE PRODUCTS OR OFFERS</div>
              <ol style={{ color: "#ddd", fontSize: 13.5, lineHeight: 1.65, margin: 0, paddingLeft: 20 }}>
                {(m.possible_products || []).filter(Boolean).map((p, i) => (
                  <li key={i} style={{ marginBottom: 8 }}>{p}</li>
                ))}
              </ol>
            </div>
            <MapCard label="EASIEST FIRST PRODUCT" value={m.easiest_first_product} testid="map-first" highlight />
            <MapCard label="CONTENT ANGLE" value={m.content_angle} testid="map-content-angle" />
            <MapCard label="YOUR NEXT STEP" value={m.your_next_step} testid="map-next-step" highlight />

            <div style={{ marginTop: 20, display: "grid", gridTemplateColumns: "1fr", gap: 10 }}>
              <a
                href="/audit"
                data-testid="map-cta-audit"
                className="btn-primary"
                style={{ textAlign: "center", padding: "16px 20px", fontSize: 14 }}
              >
                Get My Full Authority Audit — $49 <ArrowRight size={14} style={{ verticalAlign: "middle", marginLeft: 6 }} />
              </a>
              <a
                href="/interview"
                data-testid="map-cta-interview"
                className="btn-primary"
                style={{ textAlign: "center", padding: "16px 20px", fontSize: 13, background: "transparent", color: "#FF6575", border: "1px solid #FF6575" }}
              >
                Take the Deeper Guided Interview
              </a>
            </div>
            <p style={{ color: "#666", fontSize: 11, textAlign: "center", marginTop: 8 }}>
              Educational only. Not a promise of business or income outcomes. Consult qualified professionals before making business, financial, legal, or tax decisions.
            </p>
          </div>
        </section>
      </Layout>
    );
  }

  // ============ CHAT VIEW ============
  return (
    <Layout title="Atlas | The Authority Gap" path="/atlas">
      <section style={{ background: "#090909", padding: "60px 20px 32px", position: "relative", overflow: "hidden", textAlign: "center" }}>
        <HeroBg />
        <div style={{ position: "relative", zIndex: 1, maxWidth: 760, margin: "0 auto" }}>
          <div style={{ marginBottom: 20 }}><EyebrowPill>AI Strategist</EyebrowPill></div>
          <h1 className="tag-h1" style={{ marginBottom: 12, display: "flex", justifyContent: "center", alignItems: "center", gap: 12 }}>
            <Sparkles size={32} color="#FF6575" />
            <span>Atlas</span>
          </h1>
          <p style={{ color: "#aaa", fontSize: 15 }}>Talk through what you already know. Atlas will help you see what could be built from it.</p>
        </div>
      </section>

      <section style={{ background: "#090909", borderTop: "1px solid #1a1a1a", padding: "32px 20px 80px" }}>
        <div style={{ maxWidth: 760, margin: "0 auto" }}>
          <AIAssistantDisclosure assistantName="Atlas" style={{ marginBottom: 18 }} />

          <div
            ref={scrollRef}
            data-testid="atlas-thread"
            style={{
              background: "#0f0f0f",
              border: "1px solid #1a1a1a",
              borderRadius: 12,
              padding: 20,
              minHeight: 360,
              maxHeight: 560,
              overflowY: "auto",
              marginBottom: 14,
            }}
          >
            {messages.map((m, i) => (
              <div key={i} data-testid={`atlas-msg-${m.role}`} style={{ display: "flex", gap: 12, marginBottom: 18 }}>
                <div
                  style={{
                    width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
                    background: m.role === "assistant" ? "#FF6575" : "#1c1c1c",
                    border: m.role === "user" ? "1px solid #2a2a2a" : "none",
                    color: "#fff", fontSize: 11, fontWeight: 700,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}
                >
                  {m.role === "assistant" ? "A" : "You"}
                </div>
                <div style={{ flex: 1, color: m.role === "assistant" ? "#ddd" : "#bbb", fontSize: 13, lineHeight: 1.7, whiteSpace: "pre-wrap" }}>
                  {m.content}
                </div>
              </div>
            ))}
            {busy && (
              <div style={{ display: "flex", gap: 12 }}>
                <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#FF6575", color: "#fff", fontSize: 11, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>A</div>
                <div style={{ color: "#777", fontSize: 13 }} className="tag-pulse">Atlas is thinking…</div>
              </div>
            )}
          </div>

          {/* Authority signal moment — appears once Atlas has enough to work with */}
          {signalReached && !emailPromptOpen && (
            <div
              data-testid="authority-signal-cta"
              className="tag-card"
              style={{
                marginBottom: 14,
                border: "1px solid #FF6575",
                background: "linear-gradient(180deg, #1a0a0d 0%, #0f0f0f 100%)",
                padding: 20,
              }}
            >
              <div style={{ color: "#FF6575", fontSize: 9, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700, marginBottom: 10 }}>
                Atlas noticed something
              </div>
              <div style={{ color: "#fff", fontSize: 15, lineHeight: 1.5, marginBottom: 14, fontWeight: 500 }}>
                There may be something here. Want me to map it into a one-page Authority Map — what you know, who could pay for it, and the easiest first product you could build?
              </div>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <button
                  type="button"
                  className="btn-primary"
                  data-testid="build-authority-map-btn"
                  onClick={openEmailPrompt}
                  style={{ padding: "10px 18px", fontSize: 13 }}
                >
                  Build My Authority Map <ArrowRight size={13} style={{ verticalAlign: "middle", marginLeft: 4 }} />
                </button>
                <button
                  type="button"
                  data-testid="dismiss-signal-btn"
                  onClick={() => setSignalReached(false)}
                  style={{ background: "transparent", color: "#888", border: "1px solid #262626", borderRadius: 8, padding: "10px 18px", fontSize: 12, cursor: "pointer" }}
                >
                  Not yet — keep talking
                </button>
              </div>
            </div>
          )}

          {/* Email capture prompt */}
          {emailPromptOpen && !authorityMap && (
            <form
              onSubmit={generateMap}
              data-testid="authority-map-email-form"
              className="tag-card"
              style={{ marginBottom: 14, padding: 20, border: "1px solid #FF6575" }}
            >
              <div style={{ color: "#FF6575", fontSize: 9, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700, marginBottom: 10 }}>
                Where should we send your Authority Map?
              </div>
              <div style={{ color: "#bbb", fontSize: 12, marginBottom: 12 }}>
                One email. No spam. You&apos;ll get a copy of your map, and occasional notes on what to build next.
              </div>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <input
                  data-testid="authority-map-email-input"
                  type="email"
                  required
                  className="tag-input"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(ev) => setEmail(ev.target.value)}
                  style={{ flex: 1, minWidth: 220 }}
                  disabled={generating}
                />
                <button
                  type="submit"
                  className="btn-primary"
                  data-testid="generate-map-btn"
                  disabled={generating || !email.trim()}
                  style={{ padding: "10px 18px", fontSize: 13 }}
                >
                  {generating ? "Building your map…" : "Build my map"}
                </button>
                <button
                  type="button"
                  data-testid="decline-email-btn"
                  onClick={declineMap}
                  disabled={generating}
                  style={{ background: "transparent", color: "#888", border: "1px solid #262626", borderRadius: 8, padding: "10px 18px", fontSize: 12, cursor: "pointer" }}
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          <form onSubmit={send} style={{ display: "flex", gap: 10 }}>
            <input
              data-testid="atlas-input"
              className="tag-input"
              placeholder="Tell Atlas what you know how to do…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              disabled={busy || generating}
            />
            <button
              data-testid="atlas-send"
              type="submit"
              className="btn-primary"
              disabled={busy || generating || !input.trim()}
              style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
            >
              <Send size={14} /> Send
            </button>
          </form>
        </div>
      </section>
    </Layout>
  );
}

const MapCard = ({ label, value, testid, highlight }) => (
  <div
    className="tag-card"
    data-testid={testid}
    style={highlight ? { border: "1px solid #FF6575" } : undefined}
  >
    <div style={{ color: "#FF6575", fontSize: 9, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700, marginBottom: 8 }}>
      {label}
    </div>
    <div style={{ color: "#ddd", fontSize: 13.5, lineHeight: 1.65 }}>{value || "—"}</div>
  </div>
);
